import { useCallback, useEffect, useRef, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { xml as xmlLang } from '@codemirror/lang-xml'
import {
    getTemplateXml, saveTemplateXml, previewTemplatePdf, listInvoiceTemplates,
    listTemplateImages, uploadTemplateImage, deleteTemplateImage, templateImageUrl,
} from '../api'
import { useDirtyGuard, type SaveResult } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import Drawer from '../components/Drawer'
import { pdfFitWidth } from '../pdfView'
import { inactiveVersionNotice } from '../lookups'
import { TEMPLATE_RENDER_DEBOUNCE_MS, TEMPLATE_SPLIT_DEFAULT_PCT, TEMPLATE_SPLIT_MIN_PCT, TEMPLATE_SPLIT_MAX_PCT } from '../../shared/constants'
import { PAGES_INVOICE_TEMPLATE_EDITOR_TEXTS as T } from '../../shared/texts'

// Editor for ONE invoice template (opened as a tab from the list). Left: the
// config.xml (interpreted layout DSL). Right: the live PDF rendered from it with
// dummy data (debounced). Save writes the XML back; the image drawer manages the
// files in the template's version folder (referenced via <image src="…">).

export default function InvoiceTemplateEditor({ tabId, brand, version = 'version-1' }: { tabId?: string, brand: string, version?: string }) {
    const { toast } = useToast()
    const [xmlText, setXmlText] = useState('')
    const [savedXml, setSavedXml] = useState('')
    const [pdfUrl, setPdfUrl] = useState('')
    const [error, setError] = useState('')
    // Messages also as a toast — the inline notice can sit far below the fold.
    const failMsg = (msg: string) => { setError(msg); toast(msg, 'err') }
    const [warnings, setWarnings] = useState<string[]>([])
    const [rendering, setRendering] = useState(false)
    const [splitPct, setSplitPct] = useState(TEMPLATE_SPLIT_DEFAULT_PCT)
    const [renderNonce, setRenderNonce] = useState(0) // bump to force a re-render (e.g. after image upload)

    const urlRef = useRef('')
    const dirty = xmlText !== savedXml

    // Load the template XML.
    useEffect(() => {
        let alive = true
        getTemplateXml(brand, version)
            .then((x) => { if (alive) { setXmlText(x); setSavedXml(x); setError('') } })
            .catch((e) => { if (alive) failMsg(e.message) })
        return () => { alive = false }
    }, [brand, version])

    // Not the active version → say so above the editor (operator, 28.10.2026, „139"). A failed
    // list load leaves the notice out; the editor itself does not depend on it.
    const [inactiveNotice, setInactiveNotice] = useState('')
    useEffect(() => {
        let alive = true
        listInvoiceTemplates()
            .then((list) => { if (alive) setInactiveNotice(inactiveVersionNotice(list, brand, version)) })
            .catch(() => { if (alive) setInactiveNotice('') })
        return () => { alive = false }
    }, [brand, version])

    // Debounced render on XML / image change.
    useEffect(() => {
        if (!xmlText) return
        const handle = setTimeout(async () => {
            setRendering(true)
            try {
                const { pdf, warnings } = await previewTemplatePdf(xmlText, brand, version)
                const url = URL.createObjectURL(pdf)
                if (urlRef.current) URL.revokeObjectURL(urlRef.current)
                urlRef.current = url
                setPdfUrl(url)
                setWarnings(warnings)
                setError('')
            } catch (e: any) {
                failMsg(e.message ?? T.previewFailed) // keep the last good PDF
            } finally {
                setRendering(false)
            }
        }, TEMPLATE_RENDER_DEBOUNCE_MS)
        return () => clearTimeout(handle)
    }, [xmlText, brand, version, renderNonce])

    useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current) }, [])

    // Save → persist config.xml. Returns a SaveResult for the tab dirty-guard.
    const save = useCallback(async (): Promise<SaveResult> => {
        try {
            await saveTemplateXml(brand, version, xmlText)
            setSavedXml(xmlText)
            setError('')
            return { ok: true }
        } catch (e: any) {
            const msg = e?.response?.data?.error ?? e?.message ?? T.saveFailed
            failMsg(msg)
            return { ok: false, error: msg }
        }
    }, [brand, version, xmlText])
    useDirtyGuard(tabId, dirty, save)

    // Divider drag.
    const shellRef = useRef<HTMLDivElement>(null)
    const onDividerDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        const move = (ev: MouseEvent) => {
            const el = shellRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            setSplitPct(Math.min(TEMPLATE_SPLIT_MAX_PCT, Math.max(TEMPLATE_SPLIT_MIN_PCT, ((ev.clientX - rect.left) / rect.width) * 100)))
        }
        const up = () => {
            document.removeEventListener('mousemove', move)
            document.removeEventListener('mouseup', up)
            document.body.style.cursor = ''
        }
        document.addEventListener('mousemove', move)
        document.addEventListener('mouseup', up)
        document.body.style.cursor = 'col-resize'
    }, [])

    // Image drawer.
    const [imgOpen, setImgOpen] = useState(false)
    const [images, setImages] = useState<string[]>([])
    const [imgError, setImgError] = useState('')
    const [imgBusy, setImgBusy] = useState(false)

    const fileRef = useRef<HTMLInputElement>(null)
    const loadImages = useCallback(() => {
        listTemplateImages(brand, version).then(setImages).catch((e) => setImgError(e.message))
    }, [brand, version])
    const openImages = () => { setImgError(''); loadImages(); setImgOpen(true) }
    const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return
        setImgBusy(true); setImgError('')
        try {
            await uploadTemplateImage(brand, version, file)
            loadImages()
            setRenderNonce((n) => n + 1) // re-render preview in case the XML references it
        } catch (err: any) {
            setImgError(err?.response?.data?.error ?? err?.message ?? T.uploadFailed)
        } finally {
            setImgBusy(false)
            if (fileRef.current) fileRef.current.value = ''
        }
    }
    const onDeleteImage = async (file: string) => {
        setImgBusy(true); setImgError('')
        try {
            await deleteTemplateImage(brand, version, file)
            loadImages()
            setRenderNonce((n) => n + 1)
        } catch (err: any) {
            setImgError(err?.response?.data?.error ?? err?.message ?? T.deleteFailed)
        } finally {
            setImgBusy(false)
        }
    }

    return (
        <div className="tmpl-editor">
            <div className="tmpl-toolbar">
                <span className="tmpl-name">{brand} · {version}</span>
                <button type="button" onClick={() => void save()} disabled={!dirty}>
                    {dirty ? T.save : T.saved}
                </button>
                <button type="button" className="secondary" onClick={openImages}>{T.images}</button>
                <span className="tmpl-status">{rendering ? T.rendering : dirty ? T.unsaved : T.current}</span>
                <span className="muted tmpl-hint">{T.hint}</span>
            </div>
            {inactiveNotice && <div className="notice warn">{inactiveNotice}</div>}

            <div className="tmpl-split" ref={shellRef}>
                <div className="tmpl-pane tmpl-pane-editor" style={{ width: `${splitPct}%` }}>
                    <CodeMirror
                        value={xmlText}
                        height="100%"
                        extensions={[xmlLang()]}
                        onChange={setXmlText}
                        basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true }}
                    />
                    {(error || warnings.length > 0) && (
                        <div className="tmpl-messages">
                            {error && <div className="notice err">{error}</div>}
                            {warnings.length > 0 && (
                                <div className="notice warn">
                                    {warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="tmpl-divider" onMouseDown={onDividerDown} role="separator" aria-orientation="vertical" />
                <div className="tmpl-pane tmpl-pane-preview" style={{ width: `${100 - splitPct}%` }}>
                    {pdfUrl
                        ? <iframe src={pdfFitWidth(pdfUrl)} title={T.pdfPreview} className="tmpl-pdf" />
                        : <div className="muted tmpl-placeholder">{error ? T.noPreview : T.loading}</div>}
                </div>
            </div>

            <Drawer open={imgOpen} title={T.manageImages} onClose={() => setImgOpen(false)} width={420}
                action={
                    <>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden onChange={onUpload} />
                        <button type="button" onClick={() => fileRef.current?.click()} disabled={imgBusy}>{T.uploadImage}</button>
                    </>
                }>
                <p className="muted" style={{ marginTop: 0 }}>
                    {T.imagesIntro}<code>{T.imagesIntroExample}</code>.
                </p>
                {imgError && <div className="notice err">{imgError}</div>}
                {images.length === 0 ? <p className="muted">{T.noImages}</p> : (
                    <ul className="tmpl-images">
                        {images.map((f) => (
                            <li key={f}>
                                <img src={templateImageUrl(brand, version, f)} alt={f} />
                                <code>{f}</code>
                                <button type="button" className="icon-action" title={T.delete} aria-label={T.delete}
                                    disabled={imgBusy} onClick={() => void onDeleteImage(f)}>
                                    <img src="/icons/delete.svg" alt="" aria-hidden="true" />
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </Drawer>
        </div>
    )
}
